<script>
import {useMenuStore} from "@js/store/MenuStore.js";
import {useThemeStore} from "@js/store/ThemeStore.js";
import {checkDocument, getLocalStorage, setLocalStorage} from "@js/helpers.ts";
import {useJivoStore} from "@js/store/JivoStore.js";
import {contacts} from "@js/company_details.ts";
import {Link} from "@inertiajs/vue3";
import IconPhone from "@js/components/Icons/IconPhone.vue";
import IconWhatsApp from "@js/components/Icons/IconWhatsApp.vue";
import IconChatFilled from "../Icons/IconChatFilled.vue";
import IconArrowRight from "../Icons/IconArrowRight.vue";
import IconTelegram from "../Icons/IconTelegram.vue";
import IconHomeFilled from "@js/components/Icons/IconHomeFilled.vue";
import IconCartFilled from "@js/components/Icons/IconCartFilled.vue";
import IconUserCircleFilled from "@js/components/Icons/IconUserCircleFilled.vue";
import IconBurger from "@js/components/Icons/IconBurger.vue";
import IconGrid from "@js/components/Icons/IconGrid.vue";
import IconAngleLeft from "@js/components/Icons/IconAngleLeft.vue";
import IconX from "@js/components/Icons/IconX.vue";
import {useCartStore} from "@js/store/CartStore.js";
import IconMaxInner from "@js/components/Icons/IconMaxInner.vue";

export default {
    name: "MobileNav",

    components: {
        IconMaxInner,
        IconX,
        IconAngleLeft,
        IconGrid,
        IconBurger,
        IconUserCircleFilled,
        IconCartFilled,
        IconHomeFilled,
        IconTelegram,
        IconArrowRight,
        IconChatFilled,
        IconWhatsApp,
        IconPhone,
        Link,
    },

    data() {
        return {
            show_banner: false,
            speed_dial_show: false,

            touchStartX: 0,
            touchStartY: 0,
            touchEndX: 0,
            touchEndY: 0,

            scrollListener: null,
            resizeListener: null,

            lastScrollY: 0,
            isCompact: false,
            navWidth: 0,
        }
    },

    computed: {
        navStyle() {
            return {
                width: `${this.navWidth}px`,
            }
        },
    },

    methods: {
        closeBanner() {
            setLocalStorage('banner_closed', '1', 525600)
            this.show_banner = false
        },

        checkBanner() {
            return getLocalStorage('banner_closed') === '1'
        },

        expandNav() {
            if (this.isCompact) {
                this.isCompact = false
                this.updateNavWidth()
            }
        },

        toggleDial() {
            this.show_banner = false
            this.expandNav()
            this.speed_dial_show = !this.speed_dial_show
        },

        hideDial() {
            this.speed_dial_show = false
        },

        handleClickOutside(event) {
            const dropdown = this.$refs.speedDialRef
            const button = this.$refs.speedDialButtonRef

            if (
                dropdown &&
                !dropdown.contains(event.target) &&
                button &&
                !button.contains(event.target)
            ) {
                this.speed_dial_show = false
            }
        },

        onTouchStart(e) {
            this.touchStartX = e.touches[0].clientX
            this.touchStartY = e.touches[0].clientY
            this.touchEndX = e.touches[0].clientX
            this.touchEndY = e.touches[0].clientY
        },

        onTouchMove(e) {
            this.touchEndX = e.touches[0].clientX
            this.touchEndY = e.touches[0].clientY
        },

        onTouchEnd() {
            const deltaX = this.touchEndX - this.touchStartX
            const deltaY = Math.abs(this.touchEndY - this.touchStartY)
            const threshold = 50

            if (deltaX > threshold && deltaY < 50 && this.speed_dial_show) {
                this.speed_dial_show = false
            }
        },

        updateNavWidth() {
            if (!checkDocument()) return

            const viewportWidth = window.innerWidth
            const outerPadding = 24
            const fullWidth = Math.max(260, viewportWidth - outerPadding)
            const compactWidth = Math.min(340, fullWidth)

            this.navWidth = this.isCompact ? compactWidth : fullWidth
        },

        onScroll() {
            const currentScrollY = window.scrollY || 0
            const diff = currentScrollY - this.lastScrollY

            if (Math.abs(diff) > 8) {
                if (diff > 0 && currentScrollY > 20) {
                    if (!this.isCompact) {
                        this.isCompact = true
                        this.updateNavWidth()
                    }
                } else if (diff < 0) {
                    if (this.isCompact) {
                        this.isCompact = false
                        this.updateNavWidth()
                    }
                }

                this.lastScrollY = currentScrollY
            }

            if (this.speed_dial_show) {
                this.speed_dial_show = false
            }
        },

        onResize() {
            this.updateNavWidth()
        },

        openJivoChat() {
            this.expandNav()
            this.jivoStore.openJivoChat()
            this.hideDial()
        },

        openCatalog() {
            this.expandNav()
            this.menuStore.toggleSidenav()
        },

        openProfileMenu() {
            this.expandNav()
            this.menuStore.toggleProfileSidenav()
        },

        isActive(type) {
            const path = this.$page.url
            const homePath = route('home', {}, false)
            const cartPath = route('cart', {}, false)

            if (type === 'home') {
                return path === homePath
            }

            if (type === 'chat') {
                return path.startsWith('/collection') || path.startsWith('/product')
            }

            if (type === 'cart') {
                return path === cartPath || path.startsWith('/cart')
            }

            return false
        },

        navItemWrapClass(type) {
            return [
                'flex items-center justify-center rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                this.isActive(type) ? 'bg-black/15 shadow-inner' : 'bg-transparent',
                this.isCompact ? 'h-9 w-9' : 'h-10 w-10',
            ]
        },

        navIconClass(type) {
            return [
                'transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                this.isCompact ? 'h-[18px] w-[18px]' : 'h-5 w-5',
                this.isActive(type) ? 'text-black' : 'text-black/80',
            ]
        },
    },

    setup() {
        const menuStore = useMenuStore()
        const themeStore = useThemeStore()
        const jivoStore = useJivoStore()
        const cartStore = useCartStore()

        return {
            menuStore,
            themeStore,
            jivoStore,
            contacts,
            cartStore,
        }
    },

    mounted() {
        if (checkDocument()) {
            document.addEventListener("click", this.handleClickOutside)

            this.scrollListener = this.onScroll.bind(this)
            this.resizeListener = this.onResize.bind(this)

            window.addEventListener("scroll", this.scrollListener, {passive: true})
            window.addEventListener("resize", this.resizeListener, {passive: true})

            this.lastScrollY = window.scrollY || 0
            this.updateNavWidth()

            if (!this.checkBanner()) {
                this.show_banner = true
            }
        }
    },

    beforeUnmount() {
        if (checkDocument()) {
            document.removeEventListener("click", this.handleClickOutside)

            if (this.scrollListener) {
                window.removeEventListener("scroll", this.scrollListener)
            }

            if (this.resizeListener) {
                window.removeEventListener("resize", this.resizeListener)
            }
        }
    },
};
</script>

<template>
    <div
        ref="speedDialRef"
        @touchstart.passive="onTouchStart"
        @touchmove.passive="onTouchMove"
        @touchend="onTouchEnd"
        class="fixed left-1/2 z-20 w-full -translate-x-1/2 px-4 transition-all duration-300 ease-out"
        style="bottom: calc(max(env(safe-area-inset-bottom), 8px) + 72px);"
        :class="speed_dial_show ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0'"
    >
        <div
            class="mx-auto flex max-w-max items-center justify-end gap-3 rounded-full border border-white/30 bg-white/55 px-3 py-3 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.14)]"
        >
            <a
                v-if="contacts.whatsapp"
                :href="contacts.whatsapp"
                target="_blank"
                @click="hideDial"
                class="flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-white/70 text-gray-700"
            >
                <IconWhatsApp class="h-5 w-5 text-whatsapp"/>
            </a>

            <a
                v-if="contacts.max"
                :href="contacts.max"
                target="_blank"
                @click="hideDial"
                class="flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-white/70 text-gray-700"
            >
                <IconMaxInner class="h-5 w-5"/>
            </a>

            <a
                v-if="contacts.telegram"
                :href="contacts.telegram"
                target="_blank"
                @click="hideDial"
                class="flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-white/70 text-gray-700"
            >
                <IconTelegram class="h-5 w-5 text-telegram"/>
            </a>

            <button
                @click.prevent="openJivoChat"
                type="button"
                class="relative flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-white/70 text-gray-700"
            >
                <IconChatFilled class="h-5 w-5 text-gray-700"/>

                <div v-if="jivoStore.hasUnreadMessages" class="absolute top-0 right-0">
                    <div class="relative flex size-3">
                        <div
                            class="absolute inline-flex h-full w-full animate-ping rounded-full bg-navHoverColor opacity-75"></div>
                        <div class="relative inline-flex size-3 rounded-full bg-navHoverColor"></div>
                    </div>
                </div>
            </button>

            <a
                v-if="contacts.sales_department_phone"
                :href="'tel:+' + contacts.sales_department_phone"
                @click="hideDial"
                class="flex h-11 w-11 items-center justify-center rounded-full border border-white/40 bg-white/70 text-gray-700"
            >
                <IconPhone class="h-5 w-5 text-gray-700"/>
            </a>
        </div>
    </div>

    <div
        class="fixed left-0 z-10 w-full pointer-events-none px-3"
        style="bottom: max(env(safe-area-inset-bottom), 8px);"
    >
        <div v-if="show_banner" class="pointer-events-auto mx-auto mb-2 max-w-lg">
            <a
                :href="contacts.telegram"
                @click="closeBanner"
                target="_blank"
                class="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-2 pe-3 shadow-sm"
            >
                <div class="flex items-center gap-3">
                    <div class="my-2 rounded-xl bg-telegram p-2.5">
                        <IconTelegram class="h-4 w-4 text-white"/>
                    </div>
                    <p class="text-[13px] font-medium leading-tight text-black">
                        Подпишитесь на Telegram,<br> получите скидку!
                    </p>
                </div>

                <div class="flex items-center gap-3">
                    <span class="my-2 inline-flex rounded-xl bg-baseColor p-2.5">
                        <IconAngleLeft class="h-4 w-4 rotate-180 text-black"/>
                    </span>
                    <span
                        @click.prevent="closeBanner"
                        class="inline-flex h-[20px] items-center border-s border-black ps-2"
                    >
                        <IconX class="h-5 w-5"/>
                    </span>
                </div>
            </a>
        </div>

        <div
            class="pointer-events-auto mx-auto flex items-center justify-between rounded-full border border-black/20 bg-white/50 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.12)] transform-gpu origin-bottom transition-[width,padding,transform] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
            :style="navStyle"
            :class="isCompact
                ? 'px-2.5 py-1.5 scale-[0.985]'
                : 'px-3 py-2 scale-100'"
            @click.capture="expandNav"
        >
            <Link
                :href="route('home')"
                aria-label="Главная"
                title="Главная"
                class="flex items-center justify-center"
            >
                <div :class="navItemWrapClass('home')">
                    <IconHomeFilled :class="navIconClass('home')"/>
                </div>
            </Link>

            <button
                ref="speedDialButtonRef"
                @click.prevent="toggleDial"
                type="button"
                aria-label="Чат и связь"
                title="Чат и связь"
                class="relative flex items-center justify-center"
            >
                <div :class="navItemWrapClass('chat')">
                    <IconChatFilled :class="navIconClass('chat')"/>
                </div>

                <div
                    v-if="jivoStore.hasUnreadMessages && !speed_dial_show"
                    class="absolute top-0 right-0"
                >
                    <div class="relative flex size-3">
                        <div
                            class="absolute inline-flex h-full w-full animate-ping rounded-full bg-navHoverColor opacity-75"></div>
                        <div class="relative inline-flex size-3 rounded-full bg-navHoverColor"></div>
                    </div>
                </div>
            </button>

            <button
                @click.prevent="openCatalog"
                type="button"
                aria-label="Каталог"
                title="Каталог"
                class="flex items-center justify-center"
            >
                <div :class="[
                    'flex items-center justify-center rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    isCompact ? 'h-9 w-9' : 'h-10 w-10'
                ]">
                    <IconGrid :class="[
                        'text-black/80 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                        isCompact ? 'h-[18px] w-[18px]' : 'h-5 w-5'
                    ]"/>
                </div>
            </button>

            <Link
                :href="route('cart')"
                aria-label="Смета"
                title="Смета"
                class="relative flex items-center justify-center"
            >
                <div :class="navItemWrapClass('cart')">
                    <IconCartFilled :class="navIconClass('cart')"/>
                </div>

                <div
                    v-if="cartStore.items && cartStore.items.length"
                    class="absolute top-0 right-0"
                >
                    <div
                        v-text="cartStore.items.length"
                        class="flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-navHoverColor px-1 text-[9px] font-semibold leading-none text-white"
                    />
                </div>
            </Link>

            <button
                @click.prevent="openProfileMenu"
                type="button"
                aria-label="Меню"
                title="Меню"
                class="flex items-center justify-center"
            >
                <div :class="[
                    'flex items-center justify-center rounded-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                    isCompact ? 'h-9 w-9' : 'h-10 w-10'
                ]">
                    <IconBurger :class="[
                        'text-black/80 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                        isCompact ? 'h-[18px] w-[18px]' : 'h-5 w-5'
                    ]"/>
                </div>
            </button>
        </div>
    </div>
</template>

<style scoped lang="scss">
</style>
